import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertOctagon, MapPin, User, Check, Loader2, X, Map as MapIcon,
  HeartPulse, AlertCircle, Wrench, Leaf, Lock, MoreHorizontal as More,
} from 'lucide-react';
import C from '../../constants/colors';
import * as incidentsApi from '../../api/incidents';
import * as usersApi from '../../api/users';

const TYPE_META = {
  INJURY:        { label: 'Injury',        Icon: HeartPulse,  color: C.red },
  NEAR_MISS:     { label: 'Near-miss',     Icon: AlertCircle, color: C.amber },
  PROPERTY:      { label: 'Property',      Icon: Wrench,      color: C.orange },
  ENVIRONMENTAL: { label: 'Environmental', Icon: Leaf,        color: C.green },
  SECURITY:      { label: 'Security',      Icon: Lock,        color: C.purple },
  OTHER:         { label: 'Other',         Icon: More,        color: C.textSecondary },
};

const SEVERITY_META = {
  LOW:      { label: 'Low',      color: C.green },
  MEDIUM:   { label: 'Medium',   color: C.amber },
  HIGH:     { label: 'High',     color: C.orange },
  CRITICAL: { label: 'Critical', color: C.red },
};

const STATUS_META = {
  OPEN:        { label: 'Open',        color: C.red },
  IN_PROGRESS: { label: 'In Progress', color: C.amber },
  CLOSED:      { label: 'Closed',      color: C.textMuted },
};

function formatAge(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function IncidentsView({ siteId, site, zones, incidents, actions, currentUserId, onRefresh, onOpenActionForm, viewTitle = 'Incidents', defaultFilterStatus = 'OPEN', initialSelectedId = null }) {
  // If we arrived with a pre-selected item, default to ALL so it isn't filtered out
  const [filterStatus, setFilterStatus] = useState(initialSelectedId ? 'ALL' : defaultFilterStatus); // ALL | OPEN | IN_PROGRESS | CLOSED | MY_OPEN
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);
  const [photoModal, setPhotoModal] = useState(false);
  const [mapModal, setMapModal] = useState(false);

  // Users (for assignee picker) — loaded on mount
  const [users, setUsers] = useState([]);
  useEffect(() => {
    usersApi.listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  // Mutation state
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resolutionDraft, setResolutionDraft] = useState('');
  const [closeMode, setCloseMode] = useState(false);
  // Notes thread state
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState('');

  // Filter pipeline — parent already pre-filters by siteId
  const filtered = useMemo(() => {
    return (incidents || []).filter(i => {
      if (filterStatus === 'MY_OPEN') {
        if (i.status === 'CLOSED') return false;
        if (i.assignedUserId !== currentUserId) return false;
      } else if (filterStatus !== 'ALL' && i.status !== filterStatus) return false;
      if (filterType && i.incidentType !== filterType) return false;
      if (filterSeverity && i.severity !== filterSeverity) return false;
      return true;
    });
  }, [incidents, filterStatus, filterType, filterSeverity, currentUserId]);

  const selected = filtered.find(i => i.id === selectedId) || (incidents || []).find(i => i.id === selectedId) || null;

  // Reset draft state when selection changes
  useEffect(() => {
    setResolutionDraft(selected?.resolutionNote || '');
    setCloseMode(false);
    setErrorMsg('');
    setNoteDraft('');
    setNoteError('');
  }, [selectedId]);

  // ── Mutations ─────────────────────────────────────────────────
  async function handleAssign(userId) {
    if (!selected) return;
    setBusy(true); setErrorMsg('');
    try {
      await incidentsApi.assign(selected.id, userId || null);
      await onRefresh?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to assign');
    } finally { setBusy(false); }
  }

  async function handleSetStatus(newStatus) {
    if (!selected) return;
    if (newStatus === 'CLOSED') {
      // Two-step: switch to closeMode, wait for resolution note + confirm
      setCloseMode(true);
      return;
    }
    setBusy(true); setErrorMsg('');
    try {
      await incidentsApi.setStatus(selected.id, newStatus);
      await onRefresh?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to update status');
    } finally { setBusy(false); }
  }

  async function handleConfirmClose() {
    if (!selected || !resolutionDraft.trim()) return;
    setBusy(true); setErrorMsg('');
    try {
      await incidentsApi.setStatus(selected.id, 'CLOSED', resolutionDraft.trim());
      setCloseMode(false);
      await onRefresh?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to close incident');
    } finally { setBusy(false); }
  }

  async function handleAddNote() {
    if (!selected || !noteDraft.trim()) return;
    setNoteBusy(true); setNoteError('');
    try {
      await incidentsApi.addNote(selected.id, noteDraft.trim());
      setNoteDraft('');
      await onRefresh?.();
    } catch (err) {
      setNoteError(err?.response?.data?.error || 'Failed to add note');
    } finally { setNoteBusy(false); }
  }

  // ── Styles ────────────────────────────────────────────────────
  const sel = { background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '6px 10px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' };
  const pill = (active, color) => ({
    padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${active ? color : C.border}`,
    background: active ? color + '22' : 'transparent',
    color: active ? color : C.textSecondary,
  });

  // Counts for status pills
  const counts = useMemo(() => {
    const myOpen = (incidents || []).filter(i => i.assignedUserId === currentUserId && i.status !== 'CLOSED').length;
    return {
      ALL:         (incidents || []).length,
      OPEN:        (incidents || []).filter(i => i.status === 'OPEN').length,
      IN_PROGRESS: (incidents || []).filter(i => i.status === 'IN_PROGRESS').length,
      CLOSED:      (incidents || []).filter(i => i.status === 'CLOSED').length,
      MY_OPEN:     myOpen,
    };
  }, [incidents, currentUserId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header & filters */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <AlertOctagon size={20} color={C.amber} />
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>{viewTitle}</h2>
          {site && <span style={{ fontSize: '13px', color: C.textSecondary }}>· {site.name}</span>}
          <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: 'auto' }}>{filtered.length} of {(incidents || []).length}</span>
        </div>

        {/* Status pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          {[
            ['MY_OPEN',     'My Open',     C.blue,   counts.MY_OPEN],
            ['ALL',         'All',         C.textSecondary, counts.ALL],
            ['OPEN',        'Open',        STATUS_META.OPEN.color,        counts.OPEN],
            ['IN_PROGRESS', 'In Progress', STATUS_META.IN_PROGRESS.color, counts.IN_PROGRESS],
            ['CLOSED',      'Closed',      STATUS_META.CLOSED.color,      counts.CLOSED],
          ].map(([key, label, color, count]) => (
            <button key={key} onClick={() => setFilterStatus(key)} style={pill(filterStatus === key, color)}>
              {label} {count > 0 && <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {count}</span>}
            </button>
          ))}
        </div>

        {/* Type / Severity filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={sel}>
            <option value="">All types</option>
            {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} style={sel}>
            <option value="">All severities</option>
            {Object.entries(SEVERITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Body: master/detail */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* List */}
        <div style={{ width: '380px', borderRight: `1px solid ${C.border}`, overflowY: 'auto', flexShrink: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 20px', color: C.textMuted, fontSize: '13px', textAlign: 'center' }}>
              No incidents match the current filters.
            </div>
          ) : filtered.map(i => {
            const tm = TYPE_META[i.incidentType] || TYPE_META.OTHER;
            const sm = SEVERITY_META[i.severity] || SEVERITY_META.LOW;
            const isSelected = selectedId === i.id;
            return (
              <div key={i.id} onClick={() => setSelectedId(i.id)}
                style={{ display: 'flex', alignItems: 'stretch', cursor: 'pointer',
                  borderLeft: `3px solid ${sm.color}`,
                  background: isSelected ? C.surfaceElevated : 'transparent',
                  borderBottom: `1px solid ${C.border}` }}>
                <div style={{ padding: '12px 14px', flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <tm.Icon size={14} color={tm.color} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: tm.color }}>{tm.label}</span>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: sm.color, padding: '2px 6px', borderRadius: '4px', background: sm.color + '22' }}>{sm.label}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 700, color: STATUS_META[i.status].color, textTransform: 'uppercase' }}>{STATUS_META[i.status].label}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 500, marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {i.description}
                  </div>
                  <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', gap: '10px' }}>
                    <span>{i.reporterName}</span>
                    {i.siteName && <span>· {i.siteName}</span>}
                    <span style={{ marginLeft: 'auto' }}>{formatAge(i.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {!selected ? (
            <div style={{ color: C.textMuted, fontSize: '13px', textAlign: 'center', marginTop: '40px' }}>
              Select an incident to view details.
            </div>
          ) : (
            <IncidentDetail
              incident={selected}
              site={site}
              zones={zones}
              users={users}
              linkedActions={(actions || []).filter(a => a.incidentId === selected.id)}
              onOpenActionForm={onOpenActionForm}
              busy={busy}
              errorMsg={errorMsg}
              resolutionDraft={resolutionDraft}
              setResolutionDraft={setResolutionDraft}
              closeMode={closeMode}
              setCloseMode={setCloseMode}
              onAssign={handleAssign}
              onSetStatus={handleSetStatus}
              onConfirmClose={handleConfirmClose}
              onOpenPhoto={() => setPhotoModal(true)}
              onOpenMap={() => setMapModal(true)}
              noteDraft={noteDraft}
              setNoteDraft={setNoteDraft}
              noteBusy={noteBusy}
              noteError={noteError}
              onAddNote={handleAddNote}
            />
          )}
        </div>
      </div>

      {/* Photo lightbox */}
      {photoModal && selected?.photoUrl && (
        <div onClick={() => setPhotoModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', cursor: 'zoom-out' }}>
          <img src={selected.photoUrl} alt="incident" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
        </div>
      )}

      {/* Map modal */}
      {mapModal && selected && (
        <IncidentMapModal
          incident={selected}
          site={site}
          zones={zones}
          onClose={() => setMapModal(false)}
        />
      )}
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────
function IncidentDetail({ incident, site, zones, users, linkedActions, onOpenActionForm, busy, errorMsg, resolutionDraft, setResolutionDraft, closeMode, setCloseMode, onAssign, onSetStatus, onConfirmClose, onOpenPhoto, onOpenMap, noteDraft, setNoteDraft, noteBusy, noteError, onAddNote }) {
  // Map is openable if we have ANY coordinate to anchor it: incident GPS, zone, or site
  const incidentZone = (zones || []).find(z => z.id === incident.zoneId);
  const hasMapCoords =
    (incident.gpsLat != null && incident.gpsLng != null) ||
    (incidentZone?.lat != null && incidentZone?.lng != null) ||
    (site?.lat != null && site?.lng != null);
  const tm = TYPE_META[incident.incidentType] || TYPE_META.OTHER;
  const sm = SEVERITY_META[incident.severity] || SEVERITY_META.LOW;
  const stm = STATUS_META[incident.status];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <tm.Icon size={20} color={tm.color} />
        <div style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary }}>{tm.label}</div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: sm.color, padding: '3px 8px', borderRadius: '6px', background: sm.color + '22' }}>{sm.label} severity</div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {hasMapCoords && (
            <button onClick={onOpenMap}
              style={{ padding: '6px 12px', borderRadius: '6px', border: `1px solid ${C.blue}55`, background: C.blue + '22', color: C.blue, fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MapIcon size={13} />
              MAP
            </button>
          )}
          <div style={{ fontSize: '11px', fontWeight: 700, color: stm.color, padding: '3px 8px', borderRadius: '6px', background: stm.color + '22', textTransform: 'uppercase' }}>{stm.label}</div>
        </div>
      </div>

      {/* Reporter & location */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <InfoRow icon={User} label="Reporter" value={`${incident.reporterName} (${incident.reporterType === 'EMPLOYEE' ? 'Employee' : 'Visitor'})`} />
        <InfoRow icon={MapPin} label="Location" value={[incident.siteName, incident.zoneName].filter(Boolean).join(' · ') || '—'} />
      </div>

      {/* Description */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Description</div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px', color: C.textPrimary, fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {incident.description}
        </div>
      </div>

      {/* Photo */}
      {incident.photoUrl && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Photo</div>
          <img src={incident.photoUrl} alt="incident" onClick={onOpenPhoto}
            style={{ maxWidth: '320px', maxHeight: '240px', borderRadius: '10px', cursor: 'zoom-in', border: `1px solid ${C.border}`, display: 'block' }} />
        </div>
      )}

      {/* Assignee picker */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Assignee</div>
        <select
          value={incident.assignedUserId || ''}
          disabled={busy || incident.status === 'CLOSED'}
          onChange={e => onAssign(e.target.value || null)}
          style={{ width: '100%', maxWidth: '320px', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
          <option value="">— Unassigned —</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
        </select>
      </div>

      {/* Status actions */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Status</div>
        {closeMode ? (
          <div>
            <textarea
              value={resolutionDraft}
              onChange={e => setResolutionDraft(e.target.value)}
              placeholder="Resolution note (required to close)…"
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: '10px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={onConfirmClose} disabled={!resolutionDraft.trim() || busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: resolutionDraft.trim() ? C.green : C.surfaceElevated, color: resolutionDraft.trim() ? '#fff' : C.textMuted, fontSize: '13px', fontWeight: 600, cursor: resolutionDraft.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {busy ? <Loader2 size={14} className="spinning" /> : <Check size={14} />}
                Confirm Close
              </button>
              <button onClick={() => setCloseMode(false)} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {incident.status !== 'OPEN' && incident.status !== 'CLOSED' && (
              <button onClick={() => onSetStatus('OPEN')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${STATUS_META.OPEN.color}`, background: STATUS_META.OPEN.color + '22', color: STATUS_META.OPEN.color, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Reopen
              </button>
            )}
            {incident.status === 'OPEN' && incident.assignedUserId && (
              <button onClick={() => onSetStatus('IN_PROGRESS')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${STATUS_META.IN_PROGRESS.color}`, background: STATUS_META.IN_PROGRESS.color + '22', color: STATUS_META.IN_PROGRESS.color, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Mark In Progress
              </button>
            )}
            {incident.status !== 'CLOSED' && (
              <button onClick={() => onSetStatus('CLOSED')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.green}`, background: C.green + '22', color: C.green, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Close…
              </button>
            )}
            {incident.status === 'CLOSED' && (
              <button onClick={() => onSetStatus('IN_PROGRESS')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${STATUS_META.IN_PROGRESS.color}`, background: STATUS_META.IN_PROGRESS.color + '22', color: STATUS_META.IN_PROGRESS.color, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Reopen
              </button>
            )}
          </div>
        )}
      </div>

      {/* Resolution note (when closed) */}
      {incident.status === 'CLOSED' && incident.resolutionNote && !closeMode && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Resolution</div>
          <div style={{ background: C.green + '11', border: `1px solid ${C.green}33`, borderRadius: '8px', padding: '12px', color: C.textPrimary, fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {incident.resolutionNote}
          </div>
        </div>
      )}

      {/* Notes thread */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
          {(incident.notes?.length || 0) > 0 && (
            <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600 }}>· {incident.notes.length}</span>
          )}
        </div>

        {/* Input — disabled when incident is closed */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
          <textarea
            value={noteDraft || ''}
            onChange={e => setNoteDraft(e.target.value)}
            placeholder={incident.status === 'CLOSED' ? 'This incident is closed.' : 'Add a working note…'}
            rows={2}
            disabled={incident.status === 'CLOSED' || noteBusy}
            style={{ width: '100%', boxSizing: 'border-box', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: '8px', opacity: incident.status === 'CLOSED' ? 0.5 : 1 }}
          />
          {noteError && (
            <div style={{ fontSize: '12px', color: C.red, marginBottom: '6px' }}>{noteError}</div>
          )}
          <button
            onClick={onAddNote}
            disabled={!noteDraft?.trim() || noteBusy || incident.status === 'CLOSED'}
            style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', background: noteDraft?.trim() && !noteBusy && incident.status !== 'CLOSED' ? C.blue : C.surfaceElevated, color: noteDraft?.trim() && !noteBusy && incident.status !== 'CLOSED' ? '#fff' : C.textMuted, fontSize: '12px', fontWeight: 700, cursor: noteDraft?.trim() && !noteBusy && incident.status !== 'CLOSED' ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
            {noteBusy ? 'Adding…' : 'Add Note'}
          </button>
        </div>

        {/* Existing notes (newest first) */}
        {(incident.notes?.length || 0) === 0 ? (
          <div style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic', padding: '8px 4px' }}>
            No notes yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[...incident.notes].reverse().map(n => (
              <div key={n.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: C.textPrimary }}>{n.userName}</span>
                  <span style={{ fontSize: '11px', color: C.textMuted }}>· {new Date(n.createdAt).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: '13px', color: C.textPrimary, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.note}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linked corrective actions */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Corrective Actions</div>
          {(linkedActions?.length || 0) > 0 && (
            <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600 }}>· {linkedActions.length}</span>
          )}
          {onOpenActionForm && (
            <button onClick={() => onOpenActionForm({ incidentId: incident.id, siteId: incident.siteId })}
              style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${C.blue}55`, background: C.blue + '22', color: C.blue, fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Add Action
            </button>
          )}
        </div>

        {(linkedActions?.length || 0) === 0 ? (
          <div style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic', padding: '8px 4px' }}>
            No corrective actions linked yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {linkedActions.map(a => {
              const color = a.status === 'COMPLETED' ? C.green : a.status === 'IN_PROGRESS' ? C.amber : C.red;
              return (
                <div key={a.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color, padding: '2px 6px', borderRadius: '4px', background: color + '22', textTransform: 'uppercase', flexShrink: 0 }}>{a.status?.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: '13px', color: C.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.description}
                  </span>
                  {a.dueDate && (
                    <span style={{ fontSize: '11px', color: C.textMuted, flexShrink: 0, fontFamily: 'JetBrains Mono, monospace' }}>
                      due {new Date(a.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Audit trail */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Timeline</div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px', fontSize: '12px', color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.7 }}>
          <div>Reported  · {new Date(incident.createdAt).toLocaleString()}</div>
          {incident.assignedAt    && <div>Assigned   · {new Date(incident.assignedAt).toLocaleString()}</div>}
          {incident.inProgressAt  && <div>In Progress· {new Date(incident.inProgressAt).toLocaleString()}</div>}
          {incident.closedAt      && <div>Closed     · {new Date(incident.closedAt).toLocaleString()}</div>}
          {incident.gpsLat != null && incident.gpsLng != null && (
            <div>GPS        · {incident.gpsLat.toFixed(5)}, {incident.gpsLng.toFixed(5)}</div>
          )}
        </div>
      </div>

      {errorMsg && (
        <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: C.red }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px' }}>
      <div style={{ fontSize: '10px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Icon size={11} />
        {label}
      </div>
      <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

// ── Map modal ─────────────────────────────────────────────────────
// Shows the incident's location on a Leaflet map. Falls back to the
// zone's GPS, then the site's GPS, if the incident itself has no GPS.
// Reuses the same CDN-loaded Leaflet as CompanyMapView (window.L).
function IncidentMapModal({ incident, site, zones, onClose }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  // Anchor priority: incident GPS → zone GPS → site GPS
  const incidentZone = (zones || []).find(z => z.id === incident.zoneId);
  const anchor = (() => {
    if (incident.gpsLat != null && incident.gpsLng != null) {
      return { lat: incident.gpsLat, lng: incident.gpsLng, source: 'incident' };
    }
    if (incidentZone?.lat != null && incidentZone?.lng != null) {
      return { lat: incidentZone.lat, lng: incidentZone.lng, source: 'zone' };
    }
    if (site?.lat != null && site?.lng != null) {
      return { lat: site.lat, lng: site.lng, source: 'site' };
    }
    return null;
  })();

  useEffect(() => {
    if (!anchor) return;

    function init() {
      if (!mapRef.current || mapInstance.current) return;
      const L = window.L;
      const map = L.map(mapRef.current).setView([anchor.lat, anchor.lng], 18);
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY || ''}`, {
        attribution: '© CartoDB', maxZoom: 19,
      }).addTo(map);
      mapInstance.current = map;

      // Site marker (context, if available and distinct from anchor)
      if (site?.lat != null && site?.lng != null && anchor.source !== 'site') {
        L.circleMarker([site.lat, site.lng], {
          radius: 7, color: '#94a3b8', weight: 2, fillOpacity: 0.4, fillColor: '#94a3b8',
        }).addTo(map).bindTooltip(`Site: ${site.name}`, { permanent: false });
      }

      // Zone marker (context, if available and distinct from anchor)
      if (incidentZone?.lat != null && incidentZone?.lng != null && anchor.source !== 'zone') {
        L.circleMarker([incidentZone.lat, incidentZone.lng], {
          radius: 8, color: '#3b82f6', weight: 2, fillOpacity: 0.4, fillColor: '#3b82f6',
        }).addTo(map).bindTooltip(`Zone: ${incidentZone.name}`, { permanent: false });
      }

      // Incident marker — pulsing red dot with a popup
      const sev = (incident.severity || 'MEDIUM').toUpperCase();
      const sevColor = { LOW: '#22c55e', MEDIUM: '#f59e0b', HIGH: '#f97316', CRITICAL: '#ef4444' }[sev] || '#ef4444';
      const html = `<div style="width:18px;height:18px;border-radius:50%;background:${sevColor};border:3px solid #fff;box-shadow:0 0 0 4px ${sevColor}55;"></div>`;
      const icon = L.divIcon({ html, className: '', iconSize: [18, 18], iconAnchor: [9, 9] });
      L.marker([anchor.lat, anchor.lng], { icon })
        .addTo(map)
        .bindPopup(`<b>${incident.incidentType}</b><br>${incident.severity} severity<br>${incident.reporterName}`)
        .openPopup();
    }

    if (window.L) { init(); }
    else if (!document.getElementById('leaflet-script')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.id = 'leaflet-script';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = init;
      document.head.appendChild(script);
    } else {
      // Script already in flight (added by another view) — wait for it
      const t = setInterval(() => { if (window.L) { clearInterval(t); init(); } }, 80);
      return () => clearInterval(t);
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [incident?.id]);

  if (!anchor) {
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '24px', maxWidth: '400px', textAlign: 'center', color: C.textSecondary, fontSize: '14px' }}>
          No GPS location is available for this incident.
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', width: '100%', maxWidth: '900px', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MapIcon size={16} color={C.blue} />
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>Incident Location</div>
          <div style={{ fontSize: '11px', color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
            {anchor.lat.toFixed(5)}, {anchor.lng.toFixed(5)}
            <span style={{ marginLeft: '8px', color: anchor.source === 'incident' ? C.green : C.amber }}>
              ({anchor.source === 'incident' ? 'reported GPS' : `falling back to ${anchor.source} location`})
            </span>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>
        <div ref={mapRef} style={{ flex: 1, minHeight: 0 }} />
      </div>
    </div>
  );
}
